// components/layout/WorkbookLayout.jsx
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BlockRenderer from "../../renderer/BlockRenderer";
import "../../styles/workbook.css";
import apiClient from "../../api/client";
import SectionDiscussion from "./SectionDiscussion";
import SectionFeedbackPrompt, { shouldShowFeedback } from "./Sectionfeedbackprompt";
import PurchaseIntentModal from "./PurchaseIntentModal";
import WorkbookReviewPrompt from "./WorkbookReviewPrompt";
import Block from "../InteractiveBlocks";
import { workbooksApi } from "../../api/workbooks";
import { FREE_SLUGS } from "../../data/workbookBundles";
import { startCheckout } from "../../api/payment";
import { workbookProductFor, rupees } from "../../api/catalogue";
import WorkbookBreadcrumb from "../WorkbookBreadcrumb";
import { currentPath, loginUrlFor } from "../../lib/nextUrl";

// Maps a workbook.track value to its library route param and human title.
// null/"" is the csharp foundation; the three API tracks map to themselves.
const READER_TRACKS = {
  "solidworks-api": "SolidWorks API Automation",
  "autocad-api": "AutoCAD API Automation",
  "revit-api": "Revit API Automation",
};

// FREE_SLUGS is imported from the shared workbook bundle model (single source).

// ── Lock overlay ──────────────────────────────────────────────────────────────
// Unlocks the bundle this workbook belongs to.
//
// The product code and the price used to be a ternary on the track string
// ("solidworks-api" ? "wb_solidworks" : "wb_csharp") with hardcoded rupee
// fallbacks behind them, so this overlay could advertise one number while
// checkout charged another, and it could only ever offer two of the four
// tracks. Both now come from the bundle the workbook is actually in.
const LockedSection = ({
  workbookTitle,
  bundle,
  onUnlocked,
  bundleUnavailable = false,
  onRetryBundle,
}) => {
  const navigate = useNavigate();

  // The workbook-only SKU: unlocking a workbook buys the workbooks, not the
  // separately priced video course that also carries this track.
  const product = workbookProductFor(bundle);
  const price = rupees(product?.price);
  const original = rupees(product?.originalPrice);
  const saving = original != null && price != null && original > price
    ? original - price
    : null;

  // The same checkout every other buy button uses. This used to be a second
  // copy of the Razorpay flow, including its own script loader and its own
  // verify call that posted a client-chosen product back to the server.
  //
  // No redirectTo: the reader stays exactly where it is and the section unlocks
  // underneath the buyer. It used to pass window.location.pathname, which meant
  // a full document reload of this very page, and since the section lived in
  // component state rather than the URL, the reader came back at whatever
  // section the progress read chose, not the one just paid for. The section is
  // in ?s= now, so even a reload would land in the right place.
  const handleUnlock = () =>
    startCheckout({
      productCode: product?.code,
      title: product?.title,
      navigate,
      onSettled: onUnlocked,
    });

  return (
    <div className="locked-overlay">
      <div style={{ fontSize: 32, marginBottom: ".75rem" }}>🔒</div>
      <h3
        style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 20,
          fontWeight: 700,
          marginBottom: ".5rem",
        }}
      >
        This section is locked
      </h3>
      <p
        style={{
          fontSize: 14,
          color: "#71717a",
          maxWidth: 380,
          margin: "0 auto 1rem",
          lineHeight: 1.65,
        }}
      >
        {bundle?.description
          || `Unlock ${bundle?.title || "this track"} to access this section.`}
      </p>

      {price != null && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: "1.5rem",
          }}
        >
          {original != null && original > price && (
            <span style={{ fontSize: 16, color: "#a1a1aa", textDecoration: "line-through" }}>
              &#8377;{original.toLocaleString("en-IN")}
            </span>
          )}
          <span
            style={{ fontSize: 26, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Fraunces', serif" }}
          >
            &#8377;{price.toLocaleString("en-IN")}
          </span>
          {saving != null && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#059669",
                background: "#ECFDF5",
                padding: "2px 8px",
                borderRadius: 99,
                border: "1px solid #BBF7D0",
              }}
            >
              Save &#8377;{saving.toLocaleString("en-IN")}
            </span>
          )}
        </div>
      )}

      {/* The offer could not be loaded. Say that, rather than "Not on sale
          yet", which claims an answer the page never received. */}
      {bundleUnavailable && !product && (
        <div style={{ fontSize: 13, color: "#78350F", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "9px 12px", marginBottom: "1rem", maxWidth: 360, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
          We could not load this offer.{" "}
          {onRetryBundle && (
            <button
              onClick={onRetryBundle}
              style={{ background: "none", border: "none", padding: 0, color: "#78350F", fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}
            >
              Try again
            </button>
          )}
        </div>
      )}

      <button
        onClick={handleUnlock}
        disabled={!product}
        style={{
          background: "#2563eb",
          color: "white",
          border: "none",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          cursor: product ? "pointer" : "not-allowed",
          opacity: product ? 1 : 0.7,
          fontFamily: "inherit",
        }}
      >
        {!product
          ? bundleUnavailable
            ? "Unavailable right now"
            : "Not on sale yet"
          : price != null
            ? `Unlock ${product.title} \u2014 \u20B9${price.toLocaleString("en-IN")}`
            : `Unlock ${product.title}`}
      </button>
      <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: ".75rem" }}>
        Secure payment via Razorpay · Instant access
      </div>
    </div>
  );
};

// ── Checkmark SVG ─────────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M1.5 5L4 7.5L8.5 2.5"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// WorkbookLayout — main shell
// ═══════════════════════════════════════════════════════════════════════════════
// `bundle` is the catalogue bundle this workbook belongs to, fetched by the
// page. It replaces the `pricing` prop, which carried the old two-key pricing
// response that could only describe two of the four tracks.
// The value that identifies a section in ?s=.
//
// A section's own slug, when it has one. SectionSlug is a non-nullable column
// with an empty-string default, so rows that predate it can come through blank;
// those fall back to a 1-based position, which is stable enough for a URL and
// still readable.
const sectionParam = (section, index) =>
  section?.sectionSlug || String(index + 1);

const indexFromParam = (sections, raw) => {
  if (!raw) return -1;
  const bySlug = sections.findIndex((s) => s.sectionSlug && s.sectionSlug === raw);
  if (bySlug > -1) return bySlug;
  const asNumber = Number(raw);
  if (Number.isInteger(asNumber) && asNumber >= 1 && asNumber <= sections.length) {
    return asNumber - 1;
  }
  return -1;
};

const WorkbookLayout = ({
  workbook,
  userAccess = false,
  bundle,
  bundleUnavailable = false,
  onRetryBundle,
  onRefreshAccess,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sections = useMemo(() => workbook.sections || [], [workbook.sections]);
  const total = sections.length;
  const sidebarWidth = 400;

  // ── Core state ──────────────────────────────────────────────────────────────
  // Which section is open lives in ?s=, not in component state. It used to be a
  // useState, which meant the back button left the workbook entirely rather than
  // stepping back a section, a section could not be linked to or survive a
  // refresh, and a buyer who unlocked a section was returned to a different one.
  //
  // An unrecognised or missing ?s= reads as the first section, until the progress
  // load below positions the reader.
  const urlIndex = indexFromParam(sections, searchParams.get("s"));
  const current = urlIndex > -1 ? urlIndex : 0;

  // The effects below write the query string without wanting to re-run every
  // time it changes: taking searchParams as a dependency would refetch progress
  // on every page turn.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Access comes from the server. A purchase now settles in place, so this prop
  // changes under the reader when the page re-reads ownership.
  const unlocked = userAccess;
  const [completedSections, setCompletedSections] = useState(new Set());
  const [progressLoaded, setProgressLoaded] = useState(false);
  // Set when a read came back 401. The reader stays where it is and says so,
  // instead of being navigated off the page: /api/progress is [Authorize] and
  // so is the section-view telemetry below, so an expired token used to cost a
  // reader their place the moment they turned a page.
  const [sessionExpired, setSessionExpired] = useState(false);
  // Where the progress read says to resume, applied once and only when the URL
  // has not already named a section.
  const resumeIndexRef = useRef(-1);
  const positionedRef = useRef(false);

  // ── Mobile drawer ───────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Section-view tracking ───────────────────────────────────────────────────
  const sectionViewRef = useRef({ eventId: null, enteredAt: null });

  useEffect(() => {
    if (!progressLoaded) return;
    const sect = sections[current];
    if (!sect) return;

    const wbId = Number(workbook.id);
    const secId = Number(sect.id);
    if (!wbId || !secId) return;

    const enteredAt = Date.now();
    sectionViewRef.current = { eventId: null, enteredAt };

    apiClient
      .post("/api/feedback/section-view", { workbookId: wbId, sectionId: secId })
      .then((data) => { if (data?.eventId) sectionViewRef.current.eventId = data.eventId; })
      .catch((err) => { if (err?.status === 401) setSessionExpired(true); });

    return () => {
      const { eventId, enteredAt: ea } = sectionViewRef.current;
      if (eventId) {
        apiClient
          .patch(`/api/feedback/section-view/${eventId}`, {
            durationSeconds: Math.round((Date.now() - ea) / 1000),
          })
          .catch(() => {});
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, progressLoaded]); // workbook.id and sections are stable after load

  // ── Continue / feedback flow state ──────────────────────────────────────────
  const [showSectionFeedback, setShowSectionFeedback] = useState(false);
  const [showPurchaseIntent, setShowPurchaseIntent] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [ratedSections, setRatedSections] = useState(new Set());

  // ── Load progress ───────────────────────────────────────────────────────────
  useEffect(() => {
    const isMounted = { current: true };
    workbooksApi
      .getProgress(workbook.slug)
      .then((data) => {
        if (!isMounted.current) return;
        const completedIds = new Set(
          (data || []).filter((p) => p.completed).map((p) => p.sectionId),
        );
        setCompletedSections(completedIds);

        const lastCompletedIdx = sections.reduce(
          (acc, sec, i) => (completedIds.has(sec.id) ? i : acc),
          -1,
        );
        if (lastCompletedIdx > -1) {
          resumeIndexRef.current =
            lastCompletedIdx + 1 < total ? lastCompletedIdx + 1 : lastCompletedIdx;
        }
        setProgressLoaded(true);
      })
      .catch((err) => {
        if (!isMounted.current) return;
        if (err?.status === 401) setSessionExpired(true);
        setProgressLoaded(true);
      });
    return () => { isMounted.current = false; };
  }, [workbook.slug, sections, total]);

  // Resume where they left off, once, and only if they did not arrive with a
  // section already in the URL. A link, a refresh or a back button naming a
  // section always wins over the progress read.
  //
  // replace, not push: this is the reader's entry point being corrected, not a
  // move they made, so back should leave the workbook rather than return to a
  // section they never saw.
  useEffect(() => {
    if (!progressLoaded || positionedRef.current) return;
    positionedRef.current = true;
    if (indexFromParam(sections, searchParamsRef.current.get("s")) > -1) return;
    const idx = resumeIndexRef.current;
    if (idx < 0 || idx >= sections.length) return;
    const next = new URLSearchParams(searchParamsRef.current);
    next.set("s", sectionParam(sections[idx], idx));
    setSearchParams(next, { replace: true });
  }, [progressLoaded, sections, setSearchParams]);

  const pct = total > 0 ? Math.round((completedSections.size / total) * 100) : 0;
  const currentSection = sections[current];
  const isLocked = !currentSection?.isFree && !unlocked;

  // ── Navigation helpers ──────────────────────────────────────────────────────
  // Turning a page is a navigation now, so back steps one section instead of
  // leaving the workbook. Pushes, because it is the reader's own move and they
  // should be able to walk it back.
  const go = useCallback(
    (idx) => {
      if (idx < 0 || idx >= sections.length) return;
      const next = new URLSearchParams(searchParamsRef.current);
      next.set("s", sectionParam(sections[idx], idx));
      setSearchParams(next);
    },
    [sections, setSearchParams],
  );

  // Scrolling is not handled here any more. Turning a section is a real
  // navigation now (?s= pushes), so ScrollManager starts it at the top, and
  // going back restores where that section was left.
  //
  // What was here: four scroll targets written twice, immediately and again on a
  // 100ms timeout. Three of the four were no-ops - .wb-body has no overflow, so
  // contentRef was never a scroll container, and <body> is the scrolling box here
  // rather than the window or documentElement. The one that landed then fought
  // html{scroll-behavior:smooth}, which applies to scrollTop assignment too, so
  // every section change animated a long glide up the page.

  // ── Reset transient state on section change ─────────────────────────────────
  useEffect(() => {
    setDrawerOpen(false);
    setShowSectionFeedback(false);
    setShowPurchaseIntent(false);
    setShowReviewPrompt(false);
  }, [current]);

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" && current < total - 1) go(current + 1);
      if (e.key === "ArrowLeft" && current > 0) go(current - 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [current, total, go]);

  // ── Continue handler — saves progress then shows appropriate prompt ──────────
  const handleContinue = useCallback(
    () => {
      const sect = sections[current];
      if (!sect) return;

      const isLast = current === total - 1;

      // Save progress optimistically in background
      setCompletedSections((prev) => new Set([...prev, sect.id]));
      workbooksApi
        .saveProgress({ workbookId: workbook.id, sectionId: sect.id, completed: true })
        .catch((err) => console.warn("Progress save failed:", err));

      const nextSection = sections[current + 1];
      const nextIsLocked = nextSection && !nextSection.isFree && !unlocked;

      if (isLast) {
        // Bulk-complete every section that was visited but never clicked Continue
        const uncompletedIds = sections
          .map((s) => s.id)
          .filter((id) => id !== sect.id && !completedSections.has(id));

        if (uncompletedIds.length > 0) {
          setCompletedSections((prev) => new Set([...prev, ...uncompletedIds]));
          Promise.all(
            uncompletedIds.map((sectionId) =>
              workbooksApi
                .saveProgress({ workbookId: workbook.id, sectionId, completed: true })
                .catch((err) => console.warn("Progress save failed for section", sectionId, err)),
            ),
          );
        }

        setShowReviewPrompt(true);
      } else if (nextIsLocked) {
        setShowPurchaseIntent(true);
      } else if (shouldShowFeedback(sect.id, ratedSections)) {
        setShowSectionFeedback(true);
      } else {
        go(current + 1);
      }
    },
    [current, total, sections, completedSections, workbook.id, unlocked, ratedSections, go],
  );


  // ── Feedback done handlers ───────────────────────────────────────────────────
  const handleSectionFeedbackDone = useCallback(
    (submitted) => {
      if (submitted) {
        setRatedSections((prev) => new Set([...prev, sections[current]?.id]));
      }
      setShowSectionFeedback(false);
      go(current + 1);
    },
    [current, sections, go],
  );

  const handlePurchaseIntentDone = useCallback(() => {
    setShowPurchaseIntent(false);
    go(current + 1);
  }, [current, go]);

  const handleReviewDone = useCallback(() => {
    setShowReviewPrompt(false);
    navigate("/workbooks");
  }, [navigate]);

  // ── Loading / empty guards ──────────────────────────────────────────────────
  if (!progressLoaded)
    return (
      <div
        style={{
          padding: "3rem",
          textAlign: "center",
          color: "#71717a",
          fontFamily: "sans-serif",
        }}
      >
        Loading...
      </div>
    );

  if (!currentSection) return null;

  const isLastSection = current === total - 1;

  // Breadcrumb / back target derived from this workbook's track.
  // Free workbooks resolve to the Free Workbooks library first, before any
  // track-based mapping (they carry no track of their own).
  const isFree = FREE_SLUGS.includes(workbook.slug);
  const trackParam = isFree ? "free" : READER_TRACKS[workbook.track] ? workbook.track : "csharp";
  const trackTitle = isFree ? "Free Workbooks" : READER_TRACKS[workbook.track] || "C# Foundation";
  const libraryUrl = `/workbooks/bundle/${trackParam}/library`;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="wb-reader">
      {/* ── Amber progress bar ── */}
      <div className="wb-pbar">
        <div className="wb-pbar-fill" style={{ width: `${pct}%` }} />
      </div>

      {/* ── Expired session ── */}
      {/* A banner, not a redirect. The reader keeps the page and the section
          they were on; signing in again returns them here rather than dumping
          them on the courses list. Progress will not save until they do. */}
      {sessionExpired && (
        <div
          role="status"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 10,
            padding: "10px 16px",
            background: "#FEF3C7",
            borderBottom: "1px solid #FDE68A",
            color: "#78350F",
            fontSize: 13.5,
            lineHeight: 1.5,
          }}
        >
          <span>Your session expired, so your progress is not being saved.</span>
          <button
            onClick={() => navigate(loginUrlFor(currentPath()))}
            style={{
              background: "#78350F",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign in again
          </button>
        </div>
      )}

      {/* ── Mobile hamburger ── */}
      <button
        className="wb-hamburger"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open sections"
      >
        ☰
      </button>

      {/* ── Drawer backdrop (mobile) ── */}
      {drawerOpen && (
        <div
          className="wb-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* ── Left sidebar ── */}
      <aside
        className={`wb-sidebar${drawerOpen ? " drawer-open" : ""}`}
        style={{ width: sidebarWidth }}
      >
        {/* Mobile close button */}
        <button
          className="wb-drawer-close"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close sidebar"
        >
          ✕
        </button>

        <a href={libraryUrl} className="wb-sidebar-back">← Back to {trackTitle}</a>
        <div className="wb-sidebar-label">{workbook.badge || "WORKBOOK"}</div>
        <div className="wb-sidebar-wbtitle">{workbook.title}</div>
        {workbook.subtitle && (
          <div className="wb-sidebar-subtitle">{workbook.subtitle}</div>
        )}
        <div className="wb-sidebar-meta">
          {total} SECTIONS{workbook.readTime ? ` · ~${workbook.readTime}` : ""}
        </div>
        <hr className="wb-sidebar-divider" />
        {sections.map((sec, i) => {
          const isActive = i === current;
          const isDone = !isActive && completedSections.has(sec.id);
          return (
            <button
              key={sec.id}
              className={`wb-sec-item${isActive ? " active" : ""}${isDone ? " done" : ""}`}
              onClick={() => go(i)}
            >
              <span className="wb-sec-num">
                {isDone ? <CheckIcon /> : (sec.number || String(i + 1).padStart(2, "0"))}
              </span>
              <span className="wb-sec-text">{sec.title}</span>
              {!sec.isFree && !unlocked && (
                <span className="wb-sec-lock">🔒</span>
              )}
            </button>
          );
        })}
      </aside>

      {/* ── Main content ── */}
      <div className="wb-body" style={{ marginLeft: sidebarWidth }}>
        <main className="wb-content">
          {/* Breadcrumb row at the top of the reader content */}
          <WorkbookBreadcrumb
            items={[
              { label: "Workbooks", to: "/workbooks" },
              { label: trackTitle, to: libraryUrl },
              { label: workbook.title },
            ]}
          />

          {/* Section header: large serif H1 + blockquote hook (no CH badge) */}
          <div className="wb-sec-header">
            <h2 className="wb-sec-title">{currentSection.title}</h2>
            {currentSection.hook && (
              <blockquote className="wb-sec-hook">{currentSection.hook}</blockquote>
            )}
          </div>

          {/* Blocks or lock gate */}
          {isLocked ? (
            <LockedSection
              onUnlocked={onRefreshAccess}
              bundleUnavailable={bundleUnavailable}
              onRetryBundle={onRetryBundle}
              workbookTitle={workbook.title}
              bundle={bundle}
            />
          ) : (
            <div className="sec-blocks" key={current}>
              {(currentSection.blocks || []).map((block, i) =>
                [
                  "quiz",
                  "spot-bug",
                  "fill-blank",
                  "token-build",
                  "predict",
                  "text",
                  "code",
                  "callout",
                  "hint",
                  "logic-chips",
                  "comparison",
                  "types-grid",
                ].includes(block.type) ? (
                  <Block
                    key={i}
                    block={block}
                    onCorrect={(xp) => console.log("XP:", xp)}
                  />
                ) : (
                  <BlockRenderer key={i} block={block} />
                ),
              )}
            </div>
          )}

          {!isLocked && (
            <>
              <SectionDiscussion
                workbookId={workbook.slug}
                sectionId={currentSection.sectionSlug}
              />

              {/* Navigation row */}
              <div className="wb-continue-wrap">
                {current > 0 ? (
                  <button
                    className="wb-prev-btn"
                    onClick={() => go(current - 1)}
                  >
                    ← Previous
                  </button>
                ) : (
                  <span />
                )}

                {isLastSection && completedSections.has(currentSection.id) ? (
                  <button
                    disabled
                    style={{
                      background: "#059669",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 22px",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "default",
                      fontFamily: "inherit",
                      opacity: 0.85,
                    }}
                  >
                    ✓ Completed
                  </button>
                ) : (
                  <button
                    className={`wb-continue-btn${isLastSection ? " finish" : ""}`}
                    onClick={handleContinue}
                  >
                    {isLastSection ? "Mark as Complete →" : "Continue →"}
                  </button>
                )}
              </div>


            </>
          )}
        </main>
      </div>

      {/* Section feedback prompt */}
      {showSectionFeedback && (
        <SectionFeedbackPrompt
          workbookId={workbook.id}
          sectionId={currentSection?.id}
          sectionTitle={currentSection?.title}
          onDone={handleSectionFeedbackDone}
        />
      )}

      {/* Purchase intent (end of free content) */}
      {showPurchaseIntent && (
        <PurchaseIntentModal
          workbookId={workbook.id}
          onDone={handlePurchaseIntentDone}
        />
      )}

      {/* Workbook review (on completion) */}
      {showReviewPrompt && (
        <WorkbookReviewPrompt
          workbookId={workbook.id}
          workbookTitle={workbook.title}
          onDone={handleReviewDone}
        />
      )}
    </div>
  );
};

export default WorkbookLayout;
